import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { initialBoard, saveCustomSetup, loadCustomSetup, SIZE } from "../game/engine";
import Piece from "../components/Piece";
import { usePreferences } from "../context/PreferencesContext";
import { toast } from "sonner";
import { Save, RotateCcw, Trash2, Crown, Eraser } from "lucide-react";

const TARGET_PER_SIDE = 10;

// Reuse the same visual as the Board but rendered as a clickable canvas.
function SetupBoard({ board, tool, onCellClick, pieceTheme }) {
  const ys = [...Array(SIZE).keys()].reverse();
  const xs = [...Array(SIZE).keys()];
  return (
    <div className="w-full max-w-[520px] mx-auto">
      <div className="relative rounded-lg p-4 sm:p-6 bg-wood border-2 border-[#3a2410] shadow-2xl">
        <svg
          viewBox="0 0 700 700"
          className="absolute inset-4 sm:inset-6 w-[calc(100%-2rem)] sm:w-[calc(100%-3rem)] h-[calc(100%-2rem)] sm:h-[calc(100%-3rem)] pointer-events-none"
          aria-hidden
        >
          {(() => {
            const N = SIZE;
            const pos = (i) => ((i + 0.5) * 700) / N;
            return (
              <>
                {Array.from({ length: N }).map((_, i) => {
                  const p = pos(i);
                  return (
                    <g key={`g-${i}`}>
                      <line x1={p} y1={pos(0)} x2={p} y2={pos(N - 1)} stroke="#E8E2D2" strokeOpacity="0.9" strokeWidth="1.8" />
                      <line x1={pos(0)} y1={p} x2={pos(N - 1)} y2={p} stroke="#E8E2D2" strokeOpacity="0.9" strokeWidth="1.8" />
                    </g>
                  );
                })}
                {Array.from({ length: N - 1 }).flatMap((_, ix) =>
                  Array.from({ length: N - 1 }).map((_, iy) => {
                    const x1 = pos(ix), y1 = pos(iy);
                    const x2 = pos(ix + 1), y2 = pos(iy + 1);
                    return (
                      <g key={`d-${ix}-${iy}`}>
                        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#E8E2D2" strokeOpacity="0.75" strokeWidth="1.2" />
                        <line x1={x2} y1={y1} x2={x1} y2={y2} stroke="#E8E2D2" strokeOpacity="0.75" strokeWidth="1.2" />
                      </g>
                    );
                  })
                )}
              </>
            );
          })()}
        </svg>

        <div
          className="relative grid"
          style={{
            gridTemplateColumns: `repeat(${SIZE}, 1fr)`,
            gridTemplateRows: `repeat(${SIZE}, 1fr)`,
            aspectRatio: "1 / 1",
          }}
        >
          {ys.map((y) =>
            xs.map((x) => {
              const piece = board[y][x];
              return (
                <button
                  key={`${x},${y}`}
                  type="button"
                  data-testid={`setup-cell-${x}-${y}`}
                  onClick={() => onCellClick(x, y)}
                  className="relative aspect-square focus:outline-none group"
                  title={`(${x},${y})`}
                >
                  {/* Coordinate label */}
                  <span className="absolute top-0 left-1 text-[9px] text-[color:var(--text-secondary)] opacity-40 group-hover:opacity-90 pointer-events-none">
                    {x},{y}
                  </span>
                  {piece && (
                    <Piece
                      owner={piece.owner}
                      king={piece.king}
                      themeId={pieceTheme}
                      showHints={false}
                    />
                  )}
                  {/* Hover tool preview */}
                  {!piece && tool !== "eraser" && tool !== "king" && (
                    <span
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[58%] h-[58%] rounded-full opacity-0 group-hover:opacity-30 pointer-events-none"
                      style={{
                        background:
                          tool === "A"
                            ? "radial-gradient(circle at 30% 25%, #ffffff 0%, #efe8d8 40%, #c8bca0 90%)"
                            : "radial-gradient(circle at 30% 25%, #4a4a4a 0%, #1c1c1c 50%, #050505 100%)",
                      }}
                    />
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default function Setup() {
  const navigate = useNavigate();
  const { pieceTheme } = usePreferences();
  const [board, setBoard] = useState(() => loadCustomSetup() || initialBoard());
  const [tool, setTool] = useState("A"); // "A" | "B" | "king" | "eraser"

  const counts = useMemo(() => {
    let a = 0, b = 0;
    for (const row of board) for (const c of row) if (c) c.owner === "A" ? a++ : b++;
    return { a, b };
  }, [board]);

  const onCellClick = (x, y) => {
    setBoard((prev) => {
      const nb = prev.map((row) => row.map((c) => (c ? { ...c } : null)));
      const current = nb[y][x];
      if (tool === "eraser") {
        nb[y][x] = null;
      } else if (tool === "king") {
        if (current) nb[y][x] = { ...current, king: !current.king };
      } else {
        // Placing A or B
        if (current && current.owner === tool) {
          // Same colour → erase
          nb[y][x] = null;
        } else {
          // Enforce 10 cap per side
          if (tool === "A" && counts.a >= TARGET_PER_SIDE) {
            toast.warning(`Limite atteinte (${TARGET_PER_SIDE} blancs).`);
            return prev;
          }
          if (tool === "B" && counts.b >= TARGET_PER_SIDE) {
            toast.warning(`Limite atteinte (${TARGET_PER_SIDE} noirs).`);
            return prev;
          }
          nb[y][x] = { owner: tool, king: current?.king || false };
        }
      }
      return nb;
    });
  };

  const onSave = () => {
    if (counts.a !== TARGET_PER_SIDE || counts.b !== TARGET_PER_SIDE) {
      toast.error(`Il faut exactement ${TARGET_PER_SIDE} pions par joueur (actuellement A:${counts.a}, B:${counts.b}).`);
      return;
    }
    saveCustomSetup(board);
    toast.success("Configuration sauvegardée !");
    navigate("/lobby");
  };

  const onReset = () => {
    setBoard(initialBoard());
    toast.info("Configuration par défaut chargée.");
  };

  const onClearAll = () => {
    setBoard(Array.from({ length: SIZE }, () => Array(SIZE).fill(null)));
  };

  const onClearSaved = () => {
    saveCustomSetup(null);
    setBoard(initialBoard());
    toast.success("Configuration personnalisée effacée.");
  };

  const tools = [
    { id: "A", label: "Coquillage blanc", swatch: "white" },
    { id: "B", label: "Galet noir", swatch: "black" },
    { id: "king", label: "Couronner / découronner", icon: <Crown size={14} /> },
    { id: "eraser", label: "Gomme", icon: <Eraser size={14} /> },
  ];

  return (
    <div className="min-h-screen bg-tribal">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 grid md:grid-cols-[1fr_280px] gap-6 fade-up">
        <section>
          <p className="text-xs tracking-[0.3em] text-[color:var(--primary)] mb-2">CONFIGURATION</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-1">Placez vos pions</h1>
          <p className="text-xs text-[color:var(--text-secondary)] mb-5">
            Système de coordonnées <code className="px-1 rounded bg-[color:var(--surface)]">(x, y)</code> · {" "}
            <strong>x</strong> = colonne 0→4 (gauche→droite) · <strong>y</strong> = rangée 0→4 (bas→haut). Coins : (0,0) bas-gauche, (4,0) bas-droite, (0,4) haut-gauche, (4,4) haut-droite.
          </p>
          <SetupBoard board={board} tool={tool} onCellClick={onCellClick} pieceTheme={pieceTheme} />
        </section>

        <aside className="space-y-4">
          <div className="glass rounded-lg p-4">
            <h3 className="text-xs tracking-[0.2em] text-[color:var(--text-secondary)] mb-3">OUTIL</h3>
            <div className="space-y-2">
              {tools.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTool(t.id)}
                  data-testid={`setup-tool-${t.id}`}
                  className={`w-full px-3 py-2.5 rounded-md border text-sm flex items-center gap-3 text-left transition ${
                    tool === t.id
                      ? "border-[color:var(--primary)] bg-[color:var(--primary)]/10"
                      : "border-[color:var(--border)] hover:bg-[color:var(--surface)]"
                  }`}
                >
                  {t.swatch === "white" && <span className="w-5 h-5 rounded-full bg-white border border-stone-400 piece-shadow" />}
                  {t.swatch === "black" && <span className="w-5 h-5 rounded-full bg-stone-900 border border-stone-700 piece-shadow" />}
                  {t.icon && <span className="w-5 h-5 grid place-items-center text-[color:var(--primary)]">{t.icon}</span>}
                  <span>{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="glass rounded-lg p-4" data-testid="setup-counts">
            <h3 className="text-xs tracking-[0.2em] text-[color:var(--text-secondary)] mb-3">PIONS PLACÉS</h3>
            <div className="flex items-center justify-between mb-2 text-sm">
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 rounded-full bg-white border border-stone-400" /> Blancs
              </span>
              <span className={`font-semibold ${counts.a === TARGET_PER_SIDE ? "text-emerald-400" : "text-[color:var(--text-primary)]"}`} data-testid="setup-count-A">
                {counts.a}/{TARGET_PER_SIDE}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 rounded-full bg-stone-900 border border-stone-700" /> Noirs
              </span>
              <span className={`font-semibold ${counts.b === TARGET_PER_SIDE ? "text-emerald-400" : "text-[color:var(--text-primary)]"}`} data-testid="setup-count-B">
                {counts.b}/{TARGET_PER_SIDE}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <button
              onClick={onSave}
              data-testid="setup-save"
              className="w-full btn-gold py-2.5 rounded-md text-sm flex items-center justify-center gap-2"
            >
              <Save size={14} /> Sauvegarder &amp; jouer
            </button>
            <button
              onClick={onReset}
              data-testid="setup-reset"
              className="w-full px-3 py-2 rounded-md border border-[color:var(--border)] text-xs flex items-center justify-center gap-2 hover:bg-[color:var(--surface)]"
            >
              <RotateCcw size={14} /> Configuration par défaut
            </button>
            <button
              onClick={onClearAll}
              data-testid="setup-clearall"
              className="w-full px-3 py-2 rounded-md border border-[color:var(--border)] text-xs flex items-center justify-center gap-2 hover:bg-[color:var(--surface)]"
            >
              <Trash2 size={14} /> Vider le plateau
            </button>
            <button
              onClick={onClearSaved}
              data-testid="setup-clearsaved"
              className="w-full px-3 py-2 rounded-md border border-[color:var(--border)] text-xs text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]"
            >
              Effacer ma configuration enregistrée
            </button>
          </div>

          <Link to="/lobby" className="block text-center text-xs text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]" data-testid="setup-back">
            ← Retour au lobby
          </Link>
        </aside>
      </main>
    </div>
  );
}
